/**
 * Quick tips content for each settings tab
 * These provide contextual help to guide users through the songwriting process
 */

export interface QuickTip {
  icon: string
  title: string
  description: string
  learnMoreContent?: string
}

export type TabId = 'foundation' | 'boxes' | 'structure' | 'sound' | 'style' | 'ai'

export const QUICK_TIPS: Record<TabId, QuickTip[]> = {
  foundation: [
    {
      icon: '💡',
      title: 'Start with Core Questions',
      description: 'Answering Who/Whom/Why provides clarity for your entire song.',
      learnMoreContent: 'The Core Questions help establish the narrative foundation. Who is speaking? Who are they speaking to? Why are they saying this now? These answers will guide every creative decision that follows.'
    },
    {
      icon: '📹',
      title: 'POV = Intimacy Level',
      description: 'Like film shots, different POVs create different emotional distances.',
      learnMoreContent: 'First person ("I") is intimate and confessional. Second person ("you") is direct and confrontational. Third person ("he/she/they") creates observer distance. Choose based on the emotional effect you want.'
    },
    {
      icon: '👥',
      title: 'Six Best Friends',
      description: 'Who, What, When, Where, Why, How help you build concrete scenes.',
      learnMoreContent: 'Concrete details make lyrics memorable. Instead of "I felt sad," try "I sat on the kitchen floor at 3am." The Six Best Friends remind you to ground abstract emotions in specific moments.'
    }
  ],
  boxes: [
    {
      icon: '📦',
      title: 'Map Emotional Beats',
      description: 'Boxes represent turning points or realizations in your narrative.',
      learnMoreContent: 'Each box captures a key emotional moment: a realization, a shift in perspective, a turning point. Think of them as the "scenes" in your song\'s story arc.'
    },
    {
      icon: '🎨',
      title: 'Color = Order',
      description: 'Box colors (Blue→Purple→Green→Orange→Pink) show progression.',
      learnMoreContent: 'The color progression helps visualize your song\'s emotional journey. Blue often starts hopeful, purple adds complexity, green brings growth, orange introduces tension, pink resolves or reflects.'
    },
    {
      icon: '✍️',
      title: 'Start with 3 Boxes',
      description: 'Most songs have 3-5 key emotional moments. Start simple.',
      learnMoreContent: 'Don\'t overcomplicate. A verse-chorus-bridge structure naturally maps to beginning-middle-end. Start with 3 boxes, add more only if your story truly needs them.'
    }
  ],
  structure: [
    {
      icon: '🏗️',
      title: 'Section Order Matters',
      description: 'Verse-Chorus-Verse-Chorus-Bridge-Chorus is a common pattern.',
      learnMoreContent: 'This classic structure works because it balances familiarity (repeated chorus) with forward motion (new verses, surprising bridge). Feel free to deviate, but understand why the convention exists.'
    },
    {
      icon: '📏',
      title: 'Line Count Guidelines',
      description: 'Verses: 4-8 lines, Chorus: 2-4 lines (typical, not rules).',
      learnMoreContent: 'Short sections create urgency and memorability. Long sections allow for storytelling depth. Mix lengths intentionally. A 2-line chorus after an 8-line verse creates powerful contrast.'
    }
  ],
  sound: [
    {
      icon: '🎵',
      title: 'Rhyme Schemes',
      description: 'ABAB creates resolution, ABCB feels conversational.',
      learnMoreContent: 'Perfect rhymes (love/dove) feel polished but can sound predictable. Near rhymes (love/rough) feel modern and conversational. Internal rhymes add musicality without end-line constraints.'
    },
    {
      icon: '🥁',
      title: 'Prosody = Rhythm',
      description: 'Stressed syllables create the beat your listener feels.',
      learnMoreContent: 'Great lyrics "sing" naturally. Read your lines aloud and notice which syllables you emphasize. Those stressed syllables should align with the musical beats for maximum impact.'
    }
  ],
  style: [
    {
      icon: '🔑',
      title: 'Keywords Anchor Meaning',
      description: 'Repeating keywords creates thematic cohesion.',
      learnMoreContent: 'Choose 3-5 keywords that capture your song\'s essence. Return to them throughout the song, especially in the chorus. This repetition builds thematic unity and memorability.'
    },
    {
      icon: '🎯',
      title: 'Know Your Audience',
      description: 'Writing for yourself vs. others changes word choice.',
      learnMoreContent: 'Personal songs can be specific and obscure. Commercial songs need universal touchpoints. Neither is better—just be intentional about who you\'re writing for and why.'
    }
  ],
  ai: [
    {
      icon: '🤖',
      title: 'AI as Co-Writer',
      description: 'Use AI for suggestions, not replacements. You\'re the artist.',
      learnMoreContent: 'AI can help you brainstorm, find rhymes, and overcome blocks. But the creative choices are yours. Use AI to generate options, then curate with your artistic vision.'
    },
    {
      icon: '🔒',
      title: 'Your Lyrics, Private',
      description: 'Lyrics are analyzed but never stored by our AI service.',
      learnMoreContent: 'We take your creative work seriously. Your lyrics are processed for analysis and suggestions but are never stored, shared, or used to train AI models. Your art remains yours.'
    }
  ]
}

/**
 * Keyboard shortcuts available in the settings modal
 */
export interface KeyboardShortcut {
  keys: string[]
  description: string
  testId: string
}

export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  {
    keys: ['⌘', 'S'],
    description: 'Save settings',
    testId: 'shortcut-save'
  },
  {
    keys: ['⌘', 'H'],
    description: 'Toggle help sidebar',
    testId: 'shortcut-toggle-help'
  },
  {
    keys: ['⌘', '?'],
    description: 'Start guided tour',
    testId: 'shortcut-tour'
  },
  {
    keys: ['Esc'],
    description: 'Close settings',
    testId: 'shortcut-close'
  }
]
